import { useState, useCallback } from 'react';
import { PatchType } from '../../../types';

interface PatchData {
  patchId: string;
  name: string;
  patchName?: string;
  type?: string;
  storeNumber: string;
  operations?: any[];
}

interface ExtractedPatchData {
  [PatchType.PRODUCT_PRICE]: PatchData[];
  [PatchType.PRODUCT_EXCLUSION]: PatchData[];
  [PatchType.BUNDLE_PRICE]: PatchData[];
  [PatchType.BUNDLE_EXCLUSION]: PatchData[];
}

export const usePatchDataExtractor = () => {
  const [patchData, setPatchData] = useState<ExtractedPatchData>({
    [PatchType.PRODUCT_PRICE]: [],
    [PatchType.PRODUCT_EXCLUSION]: [],
    [PatchType.BUNDLE_PRICE]: [],
    [PatchType.BUNDLE_EXCLUSION]: [],
  });

  const extractPatchDataFromStores = useCallback((stores: any[]) => {
    const extractedData: ExtractedPatchData = {
      [PatchType.PRODUCT_PRICE]: [],
      [PatchType.PRODUCT_EXCLUSION]: [],
      [PatchType.BUNDLE_PRICE]: [],
      [PatchType.BUNDLE_EXCLUSION]: [],
    };
    
    stores.forEach((store) => {
      if (store.assignedMenuDefinitions) {
        store.assignedMenuDefinitions.forEach((menuDef: any) => {
          if (menuDef.patches) {
            menuDef.patches.forEach((patch: any) => {
              // Check each patch type
              Object.values(PatchType).forEach((patchType) => {
                if (patch.name && patch.name.endsWith(patchType)) {
                  extractedData[patchType].push({
                    ...patch,
                    storeNumber: store.storeNumber,
                    type: patchType,
                  });
                }
              });
            });
          }
        });
      }
    });

    setPatchData(extractedData);
    return extractedData;
  }, []);

  const getPatchDataForType = useCallback((patchType: PatchType): PatchData[] => {
    return patchData[patchType] || [];
  }, [patchData]);

  const getPatchIdsForType = useCallback((patchType: PatchType): string[] => {
    return patchData[patchType]?.map(patch => patch.patchId) || [];
  }, [patchData]);

  const clearPatchData = useCallback(() => {
    setPatchData({
      [PatchType.PRODUCT_PRICE]: [],
      [PatchType.PRODUCT_EXCLUSION]: [],
      [PatchType.BUNDLE_PRICE]: [],
      [PatchType.BUNDLE_EXCLUSION]: [],
    });
  }, []);

  return {
    patchData,
    extractPatchDataFromStores,
    getPatchDataForType,
    getPatchIdsForType,
    clearPatchData,
  };
};